import { Injectable } from "@angular/core";
import { AxiosRequestConfig } from 'axios';

import { ApiResponse } from "src/app/shared/interfaces/api-response";
import { MemberDetails, MemberDetailsPage } from "src/app/shared/interfaces/member-details";
import { NewStrike } from "src/app/shared/interfaces/new-strike";
import { StrikeDetail, StrikeDetails } from "src/app/shared/interfaces/strike-details";
import { StrikeHouse, StrikeHouses } from "src/app/shared/interfaces/strike-houses";
import { StrikePicketLine, StrikePicketLines } from "src/app/shared/interfaces/strike-picketlines";
import { StrikeShift, StrikeShifts } from "src/app/shared/interfaces/strike-shifts";
import { UpdatedStrike } from "src/app/shared/interfaces/updated-strike";
import { WalkHistoryDetails } from "src/app/shared/interfaces/walk-history-details";
import { WalkSummary } from "src/app/shared/interfaces/walk-summary";
import { WeekSummary } from "src/app/shared/interfaces/week-summary";

import { ApiService } from "./api.service";

@Injectable({
  providedIn: 'root'
})
export class StrikeService {

  constructor(private apiService: ApiService) { }

  async getCurrentStrikes() {
    const response: ApiResponse = await this.apiService.get<StrikeDetails>('/api/strikes/current');
    if (response.success) {
      return response.body;
    }
    // ApiService will have logged error; caller is expecting list
    return [];
  }

  async getAllStrikes(config?: AxiosRequestConfig) {
    const response: ApiResponse = await this.apiService.get<StrikeDetails>('api/strikes/', config);

    if (response.success) {
      return response.body
    }

    return []
  }

  async getStrikeDetails(paramId: string | number) {
    const response: ApiResponse = await this.apiService.get<StrikeDetail>('/api/strikes/details/' + paramId)
    if (response.success) {
      return response.body
    }
    // ApiService will have logged error; caller is expecting object
    return {}
  }

  async getStrikeHouses(paramId: string | number) {
    const response: ApiResponse = await this.apiService.get<StrikeHouses>('/api/strikehouses/strike/' + paramId)
    if (response.success) {
      return response.body
    }
    // ApiService will have logged error; caller is expecting list
    return []
  }

  async getStrikePicketlines(paramId: string | number) {
    const response: ApiResponse = await this.apiService.get<StrikePicketLines>('/api/strikepicketlines/strike/' + paramId)
    if (response.success) {
      return response.body
    }
    // ApiService will have logged error; caller is expecting list
    return []
  }

  async getStrikeShifts(paramId: string | number) {
    const response: ApiResponse = await this.apiService.get<StrikeShifts>('/api/strikeshifts/strike/' + paramId)
    if (response.success) {
      return response.body
    }
    // ApiService will have logged error; caller is expecting list
    return []
  }

  async getWalkSummary(paramId: string | number) {
    const response: ApiResponse = await this.apiService.get<WalkSummary>('/api/strikes/walksummary/' + paramId)
    if (response.success) {
      return response.body
    }
    // ApiService will have logged error; caller is expecting list
    return []
  }

  async getWeekSummary(paramId: string | number) {
    const response: ApiResponse = await this.apiService.get<WeekSummary>('/api/strikes/weeksummary/' + paramId)
    if (response.success) {
      return response.body
    }
    // ApiService will have logged error; caller is expecting list
    return []
  }

  // TODO: KLD: I think config? is legacy optional pagination-related params
  // that should be explicitly added to param list with defaults for page 1
  async getWalkHistory(paramId: string | null, startDate: string, endDate: string, config?: AxiosRequestConfig) {
    const response: ApiResponse = await this.apiService.get<WalkHistoryDetails>('/api/strikewalk/history/' + paramId + '/date/' + startDate + '/' + endDate + '/', config);
    if (response.success) {
      return response.body
    }
    // ApiService will have logged error; caller is expecting object
    // (object has nested list under key 'Items')
    return {}
  }

  async getAllStrikeHouses() {
    const response: ApiResponse = await this.apiService.get<StrikeHouses>('/api/sepuededata/houses');
    if (response.success) {
      return response.body
    }
    // ApiService will have logged error; caller is expecting list
    return []
  }

  async getStrikeMembers(strikeHouseId: number ,config: {params: {page: number, pageSize: number, filter: string}}){
    const response: ApiResponse = await this.apiService.get<MemberDetailsPage>('/api/strikemembers/strikehouse/' + strikeHouseId, config)
    if(!response.success){
      return []
    }
    return response.body
  }

  // for C-UD operations just return ApiResponse, let front end do error checking
  async addStrikeHouse(newStrikeHouse: StrikeHouse) {
    const response: ApiResponse = await this.apiService.post<StrikeHouse>('/api/strikehouses/add', newStrikeHouse);
    return response
  }

  // for C-UD operations just return ApiResponse, let front end do error checking
  async addStrikeShift(newStrikeShift: StrikeShift) {
    const response: ApiResponse = await this.apiService.post<StrikeShift>('/api/strikeshifts/add', newStrikeShift);
    return response
  }

  // for C-UD operations just return ApiResponse, let front end do error checking
  async addPicketline(newPicketline: StrikePicketLine) {
    const response: ApiResponse = await this.apiService.post<StrikePicketLine>('/api/strikepicketlines/add', newPicketline);
    return response
  }

  async addStrike(newStrike: NewStrike) {
    const response: ApiResponse = await this.apiService.post('/api/strikes/add', newStrike);
    return response
  }
  
  async updateStrike(updatedStrike: UpdatedStrike) {
    const response: ApiResponse = await this.apiService.post('/api/strikes/update', updatedStrike);
    return response;
  }

  async getStrikeRegistration(houseId: string, config?: AxiosRequestConfig) {
    const response: ApiResponse = await this.apiService.get<MemberDetailsPage>('/api/strikemembers/registration/house/' + houseId, config);
    if (response.success) {
      return response.body
    }

    return []
  }

  async updateDebitCard(member: MemberDetails) {
    const response: ApiResponse = await this.apiService.post('api/strikemembers/registration/updatedebitcard', member);
    return response;
  }
  
  async refreshData(house: StrikeHouse) {
    const response: ApiResponse = await this.apiService.post('/api/strikehouses/refresh', house);
    return response;
  }
}
